import React, { useState, useEffect } from 'react';
import moment from 'moment';
import Modal from "../../../../Components/Modal";

const ApiKeyForm = ({ onClose, onSave, editingKey = null }) => {
  const [formData, setFormData] = useState({
    naam: '',
    role: 'readonly',
    scope: 'full',
    vervaldatum: moment().add(30, 'days').format('YYYY-MM-DD'),
    actief: true
  });
  const [loading, setLoading] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [generatedKey, setGeneratedKey] = useState('');

  useEffect(() => {
    if (editingKey) {
      setFormData({
        naam: editingKey.naam || '',
        role: editingKey.role || 'readonly',
        scope: editingKey.scope || 'full',
        vervaldatum: editingKey.vervaldatum ? moment(editingKey.vervaldatum).format('YYYY-MM-DD') : '',
        actief: editingKey.actief !== false
      });
    }
  }, [editingKey]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await onSave(formData, editingKey?._id);
      
      if (result.success) {
        if (!editingKey && result.result?.key) {
          setGeneratedKey(result.result.key);
          setShowKey(true);
        } else {
          onClose();
        }
      }
    } catch (error) {
      console.error('Fout bij opslaan API key:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedKey).then(() => {
      alert('API key gekopieerd naar klembord!');
    });
  };

  const roleOptions = [
    { value: 'readonly', label: 'Read-only', description: 'Alleen lezen toegang' },
    { value: 'medewerker', label: 'Medewerker', description: 'Basis CRUD operaties' },
    { value: 'manager', label: 'Manager', description: 'Uitgebreide toegang' },
    { value: 'admin', label: 'Admin', description: 'Volledige toegang' }
  ];

  const scopeOptions = [
    { value: 'full', label: 'Volledig', description: 'Alle API endpoints conform de rol' },
    { value: 'widget', label: 'Alleen widget', description: 'Alleen /api/widget/* endpoints (publieke events widget)' }
  ];


  return (
    <Modal visible={true} onClose={onClose} width={700}>
      <div className="p-4">
        <div className="mb-4">
          <h5>
            <i className="fas fa-key me-2"></i>
            {editingKey ? 'API Key Bewerken' : 'Nieuwe API Key'}
          </h5>
        </div>

        {/* Success message with generated API key */}
        {showKey && generatedKey && (
          <div className="mb-4">
            <div className="alert alert-success border-0 p-3 mb-3" style={{ backgroundColor: '#6BA368', color: 'white' }}>
              <h5 className="mb-0">
                <i className="fas fa-key me-2"></i>
                API Key Succesvol Aangemaakt
              </h5>
            </div>
            
            <div className="alert alert-warning border-0" style={{ backgroundColor: '#E1B339', color: 'white' }}>
              <i className="fas fa-exclamation-triangle me-2"></i>
              <strong>Belangrijk:</strong> Deze API key wordt slechts één keer getoond. 
              Zorg ervoor dat je deze opslaat op een veilige plaats.
            </div>
            
            <div className="form-group mb-3">
              <label className="form-label">
                <strong>API Key:</strong>
              </label>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  value={generatedKey}
                  readOnly
                  style={{ fontFamily: 'monospace', fontSize: '12px' }}
                />
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={copyToClipboard}
                  style={{ backgroundColor: '#B1BF41', borderColor: '#B1BF41' }}
                >
                  <i className="fas fa-copy"></i>
                </button>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-4">
                <p><strong>Naam:</strong> {formData.naam}</p>
              </div>
              <div className="col-md-4">
                <p><strong>Rol:</strong> {roleOptions.find(r => r.value === formData.role)?.label}</p>
              </div>
              <div className="col-md-4">
                <p><strong>Vervaldatum:</strong> {moment(formData.vervaldatum).format('DD-MM-YYYY')}</p>
              </div>
            </div>

            <div className="text-center mb-4">
              <button
                type="button"
                className="btn btn-primary"
                onClick={onClose}
                style={{ backgroundColor: '#B1BF41', borderColor: '#B1BF41' }}
              >
                <i className="fas fa-check me-2"></i>
                Sluiten
              </button>
            </div>
            
            <hr />
          </div>
        )}

        {/* Form - hide when API key is generated */}
        {!showKey && (
          <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group mb-3">
                    <label className="form-label">
                      Naam <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="naam"
                      value={formData.naam}
                      onChange={handleChange}
                      required
                      placeholder="Bijv: Mobile App, Webhook Service"
                    />
                    <small className="form-text text-muted">
                      Beschrijvende naam voor deze API key
                    </small>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-group mb-3">
                    <label className="form-label">
                      Rol <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      required
                    >
                      {roleOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <small className="form-text text-muted">
                      {roleOptions.find(r => r.value === formData.role)?.description}
                    </small>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="form-group mb-3">
                    <label className="form-label">
                      Scope <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      name="scope"
                      value={formData.scope}
                      onChange={handleChange}
                      required
                    >
                      {scopeOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <small className="form-text text-muted">
                      {scopeOptions.find(s => s.value === formData.scope)?.description}
                    </small>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-group mb-3">
                    <label className="form-label">
                      Vervaldatum <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      name="vervaldatum"
                      value={formData.vervaldatum}
                      onChange={handleChange}
                      required
                      min={moment().format('YYYY-MM-DD')}
                    />
                    <small className="form-text text-muted">
                      {formData.vervaldatum && `Verloopt over ${moment(formData.vervaldatum).diff(moment(), 'days')} dagen`}
                    </small>
                  </div>
                </div>

                <div className="col-md-6">
                  {editingKey && (
                    <div className="form-group mb-3">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          name="actief"
                          id="actief"
                          checked={formData.actief}
                          onChange={handleChange}
                        />
                        <label className="form-check-label" htmlFor="actief">
                          API key actief
                        </label>
                      </div>
                      <small className="form-text text-muted">
                        Uitgeschakelde keys kunnen niet gebruikt worden
                      </small>
                    </div>
                  )}
                </div>
              </div>

              <div className="alert alert-info border-0" style={{ backgroundColor: '#639A67', color: 'white' }}>
                <i className="fas fa-info-circle me-2"></i>
                <strong>Veiligheidsrichtlijnen:</strong>
                <ul className="mb-0 mt-2">
                  <li>API keys krijgen toegang tot jouw bedrijfsgegevens</li>
                  <li>Deel API keys nooit in onbeveiligde omgevingen</li>
                  <li>Gebruik voor elke toepassing een aparte API key</li>
                  <li>Controleer regelmatig welke keys nog actief gebruikt worden</li>
                </ul>
              </div>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
              style={{ backgroundColor: '#D96A29', borderColor: '#D96A29' }}
            >
              Annuleren
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
              style={{ backgroundColor: '#B1BF41', borderColor: '#B1BF41' }}
            >
              {loading && <i className="fas fa-spinner fa-spin me-2"></i>}
              {editingKey ? 'Bijwerken' : 'Aanmaken'}
            </button>
          </div>
        </form>
        )}
      </div>
    </Modal>
  );
};

export default ApiKeyForm;